import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import type { ReactElement } from "react";
import { Assistant } from "@/components/Assistant";
import { Spinner } from "@/components/Spinner";
import { TopNav } from "@/components/TopNav";
import { Account } from "@/pages/Account";
import { Browse } from "@/pages/Browse";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { Home } from "@/pages/Home";
import { Landing } from "@/pages/Landing";
import { LinkDevice } from "@/pages/LinkDevice";
import { Profiles } from "@/pages/Profiles";
import { ResetPassword } from "@/pages/ResetPassword";
import { Search } from "@/pages/Search";
import { SignIn } from "@/pages/SignIn";
import { SignUp } from "@/pages/SignUp";
import { TitleDetail } from "@/pages/TitleDetail";
import { VerifyEmail } from "@/pages/VerifyEmail";
import { Watchlist } from "@/pages/Watchlist";
import { useSession } from "@/state/session";

// hls.js is ~500kB and only the player needs it, so the route is split out
// and fetched when a viewer actually presses play.
const Player = lazy(() => import("@/pages/Player").then((m) => ({ default: m.Player })));

function RequireSession({ children }: { children: ReactElement }) {
  const { account, loading } = useSession();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!account) return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  return children;
}

function Shell({ children }: { children: ReactElement }) {
  return (
    <div className="app">
      <TopNav />
      {children}
      <Assistant />
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      {/*
        Account confirmation and recovery. All four are reachable without a
        session by definition - the person cannot sign in yet, or at all - and
        the two that carry a token read it from the query string, because that
        is the shape of a link in an email.
      */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/link" element={<LinkDevice />} />
      <Route
        path="/profiles"
        element={
          <RequireSession>
            <Profiles />
          </RequireSession>
        }
      />
      <Route
        path="/watch/:titleId"
        element={
          <RequireSession>
            <Suspense fallback={<Spinner label="Preparing the player" />}>
              <Player />
            </Suspense>
          </RequireSession>
        }
      />
      {[
        { path: "/home", element: <Home /> },
        { path: "/browse", element: <Browse /> },
        { path: "/search", element: <Search /> },
        { path: "/watchlist", element: <Watchlist /> },
        { path: "/account", element: <Account /> },
        { path: "/title/:titleId", element: <TitleDetail /> },
      ].map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <RequireSession>
              <Shell>{route.element}</Shell>
            </RequireSession>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
